// WC TELEMETRY — first-party page behaviour, one beacon per pageview.
//
// Collects what Shopify's own analytics cannot see inside a page: what gets clicked,
// how far people scroll, how long they actually stay. Aggregated server-side per DAY
// (engine api/track.js) — no per-user storage, no cookies, no PII, and it respects
// Do Not Track. sessionStorage carries only a "this tab already counted a session" flag.
//
// Click keys are coarse on purpose ("nav:tv", "product:no-off-switch-tee", "chet:open"):
// the question the data answers is "which SURFFACES earn attention", not "what did one
// person do". Keys must match api/track.js CLICK_RE: /^[a-z]{2,12}:[a-zA-Z0-9\-_/.]{1,60}$/.
//
// ---------------------------------------------------------------------------------------
// TWO CHANNELS, and the second one exists because the first one loses the events that matter.
//
//   1. THE PAGEVIEW BATCH (unchanged): path, dwell, scroll and the accumulated `clicks` map,
//      sent once on pagehide. Cheap, and losing one is explicitly fine.
//   2. THE SINGLE EVENT (`{ ev, p }`, new): one sendBeacon the moment it happens, for the
//      five events that decide whether this store works — the door and the till.
//
// The batch cannot carry them. `send()` is a one-shot: the first `visibilitychange → hidden`
// fires it and sets `sent = true`, so a visitor who switches tabs and comes back has already
// spent their beacon — every click after that is collected into a map that is never sent
// again. For a page-attention metric that is noise. For "did they open Chet's door" and "did
// they reach the register" it is silent, biased loss, and the door number is now load-bearing
// (the front door is plain for everyone; the parody is opt-in). The single-event channel also
// removes any dependence on pagehide surviving the mode toggle's navigation, which is the one
// place a batch beacon has to race a `location.href` assignment.
//
// It costs one request per event. That is affordable precisely because these events are rare
// by definition — if `checkout:start` ever gets frequent enough to matter to the Shopify
// quota, the store has bigger and better problems. api/track.js accepts `ev` ONLY from the
// closed vocabulary below, so this cannot become a general-purpose write channel.
//
// ---------------------------------------------------------------------------------------
// HOW THE VISITOR GOT INTO THIS COLUMN (`src`, 2026-08-17).
//
// WHICH storefront a view happened on has always ridden the PATH: plain views are reported
// under a /plain prefix (see pathNow), which is why api/track.js and lib/webstats.js can split
// the columns with a string test and no extra field. That mechanism carries one bit and it is
// already spent. The new question is a second, independent one — was this visitor ASSIGNED to
// the parody by theme.exhibit.liquid's holdout, or did they press the banner button — and
// there is no honest way to fold it into the same prefix. Encoding it as another path segment
// would rewrite the pv keys every other reader of the day-file depends on: top pages would
// split one URL into two rows, and lib/abreport.js's NOT_COMPARABLE regex, which is anchored
// at ^/, would stop recognising the lore wing and quietly admit it into the comparison.
//
// So it travels as its own field on the SAME beacons — both channels, one line each — rather
// than as a second request or a second endpoint. Values are exactly "assigned", "self" or
// "link" (the third since 2026-09-14: theme.exhibit.liquid's LINK DOOR stamps a first-time
// ?view=weird arrival — someone who followed one of the character's posts — and it is neither
// an arm nor a volunteer, so it must reach the report under its own name); anything else,
// including the absence of the key, is sent as nothing at all. THAT ABSENCE IS MEANINGFUL AND
// MUST NOT BE FILLED IN: a visitor with storage blocked is never assigned and never stamped,
// and so is everyone who arrived before the holdout shipped. Guessing "self" for them would
// pool the unknown into the volunteer column and hand the report a confident number built on
// an assumption. Unstamped is its own bucket, all the way through.
//
// EXCLUSIONS ARE STRUCTURAL, not repeated. Everything here lives inside boot(), which only
// runs once DNT, the theme editor, the owner flags and prerendering have all been cleared —
// so `window.wcTrack` simply DOES NOT EXIST for an excluded visitor, and every caller
// (wc-mode.liquid) is a no-op by construction rather than by remembering to check.
(function () {
  "use strict";
  if (navigator.doNotTrack === "1" || window.doNotTrack === "1") return;
  if (window.Shopify && Shopify.designMode) return; // theme editor previews aren't visitors

  /* THE OWNER IS NOT A VISITOR (Josh, 2026-08-16: "I'm on the site a lot, just clicking
     around, testing things, and I think we're counting all that").

     He was right, and it was not merely noise: he tests almost entirely in the parody, and
     he leaves tabs open while working, so his sessions land in exactly one column of the
     A/B and drag its dwell up. On a window holding a few hundred views, a day of his own
     browsing can decide the verdict.

     Two independent switches, because either alone has a hole:
      · a DURABLE per-device flag, set by visiting any page with ?wcoptout=1 (and cleared
        with ?wcoptout=0). Survives navigation, mode switches and sign-out. Set it once per
        browser — phone included. It is stored, not inferred, so it cannot be wrong.
      · an AUTOMATIC signal from Liquid: window.wcOwner is set true when the logged-in
        customer is the owner (see the layouts). Covers the case where he forgets the flag
        on a new device but is signed in.

     Deliberately NOT done by user agent or IP: agents lie and phones roam, and this file's
     whole contract is that it stores nothing about anyone. */
  try {
    var q = location.search;
    if (q.indexOf("wcoptout=1") > -1) { localStorage.setItem("wc_notrack", "1"); }
    if (q.indexOf("wcoptout=0") > -1) { localStorage.removeItem("wc_notrack"); }
    if (localStorage.getItem("wc_notrack") === "1") return;
  } catch (e) { /* storage blocked → fall through; the owner flag below may still catch it */ }
  if (window.wcOwner === true) return;

  // PRERENDERED PAGES ARE NOT VISITS. The motion layer speculatively prerenders the tab
  // destinations; until the user actually activates one, nothing here may run — the dwell
  // clock would start early, the session flag would burn, and a discarded prerender would
  // fire a phantom pageview on its way out. Boot everything on activation instead.
  if (document.prerendering) {
    document.addEventListener("prerenderingchange", boot, { once: true });
  } else {
    boot();
  }
  function boot() {
  // A head guard is replacing this page with the other skin (window.wcLeaving): it is not a view.
  if (window.wcLeaving) return;

  var URL = "https://api.weirdcastle.com/api/track";
  var t0 = Date.now();
  var maxScroll = 0;
  var clicks = {};
  var sent = false;

  var newSession = false;
  try {
    if (!sessionStorage.getItem("wc_t")) { sessionStorage.setItem("wc_t", "1"); newSession = true; }
  } catch (e) { /* storage blocked → every page looks like a session start; acceptable */ }

  /* THE ASSIGNMENT STAMP, read once at boot and never re-read. That is deliberate: the mode
     toggle writes 'self' and then navigates, so a value read at SEND time would relabel the
     switch itself. Read here, the "put him back" beacon that an assigned visitor fires still
     carries `assigned` — which is the whole point of measuring it, because dropping out of
     the holdout is the holdout's cost and it belongs to the group that paid it. The next page
     they load reads 'self' and everything after that is theirs.
     Read-only, and whitelisted rather than passed through: this file's contract is that it
     stores nothing about anyone, and nothing here writes wc_mode_src.
     THE LIST IS THE WIRE'S FIRST HALF. api/track.js SRC_VALUES is the second, and lib/webstats.js
     assignmentSplit() plus lib/abreport.js linkReferred() are the readers at the far end; a value
     added to one list and not the other is counted as unstamped, silently. 'link' joined all of
     them 2026-09-14 — before that the LINK DOOR routed a referred visitor as weird while this
     line dropped the stamp on the floor and the report counted them as nobody's. */
  var modeSrc = null;
  try {
    var rawSrc = localStorage.getItem("wc_mode_src");
    if (rawSrc === "assigned" || rawSrc === "self" || rawSrc === "link") modeSrc = rawSrc;
  } catch (e) { /* storage blocked → unstamped, which is the truth about this visitor */ }

  // ---- scroll depth (percent of document actually reached) ----
  function onScroll() {
    var doc = document.documentElement;
    var total = doc.scrollHeight - window.innerHeight;
    if (total <= 0) { maxScroll = 100; return; }
    var pct = Math.round(((window.scrollY || doc.scrollTop) / total) * 100);
    if (pct > maxScroll) maxScroll = Math.min(100, pct);
  }
  addEventListener("scroll", onScroll, { passive: true });
  setTimeout(onScroll, 1500);

  // The path exactly as the batch reports it, so a single event lands in the same column.
  // 2026-10-09: the prefix follows the skin that RENDERED (theme.plain stamps data-wc-skin="plain"
  // on <html>), not the ?view=plain param. Since the 08-29 swap the bare url IS the plain store, and
  // keying on the param filed every bare plain view under the weird column — the 30-day "weird 0.8%
  // vs plain 6.1%" split compared ?view=plain urls against everything else, plain pages included.
  // The param stays as a fallback for theme.liquid's policy pages, which carry no stamp.
  var PLAIN_SKIN = document.documentElement.getAttribute("data-wc-skin") === "plain";
  function pathNow() {
    return ((PLAIN_SKIN || location.search.indexOf("view=plain") > -1 ? "/plain" : "") + location.pathname).slice(0, 80);
  }

  /* THE FIVE. Anything in here is delivered on its own beacon, immediately, and is NOT added
     to the `clicks` map — exactly one delivery per event, whichever route detected it.
     api/track.js keeps the same list and folds them into a per-mode `funnel` map. */
  var IMMEDIATE = {
    "mode:release": 1,   // plain → parody: "release him". Someone opened Chet's door.
    "mode:contain": 1,   // parody → plain: "put him back".
    "cart:add": 1,
    "checkout:start": 1,
    "email:signup": 1,
    "assist:open": 1,
    "assist:send": 1,
    "assist:reply": 1,
    "assist:error": 1,
    "assist:product": 1,
    "assist:policy": 1,
    "assist:order": 1,
    "assist:discount": 1,
    "assist:sizing": 1,
    "assist:privacy": 1,
    "assist:other": 1,
  };
  var KEY_RE = /^[a-z]{2,12}:[a-zA-Z0-9\-_/.]{1,60}$/;

  /** Fire one event now. Exposed as window.wcTrack — see the exclusions note in the header. */
  function fire(key) {
    if (!KEY_RE.test(String(key))) return;
    var body = { ev: key, p: pathNow() };
    if (modeSrc) body.src = modeSrc;   // omitted, never guessed — see the `src` note above
    try { navigator.sendBeacon(URL, JSON.stringify(body)); } catch (e) {}
  }
  window.wcTrack = fire;

  // ---- click classification, delegated ----
  function keyFor(el) {
    var tagged = el.closest("[data-wc-track]");
    if (tagged) return String(tagged.getAttribute("data-wc-track")).slice(0, 70);
    var a = el.closest("a[href]");
    /* THE REGISTER. Every control on either skin that starts a checkout: the vendor theme's
       submit button, the floating cart bar, both cart pages' links, the header drawer's
       data-checkout, and Buy Now (which adds and then sends you straight there). Checked
       before the link rules below so `a[href="/checkout"]` never falls through to them. */
    if (el.closest('[name="checkout"], a[href^="/checkout"], [data-checkout], #pd-buynow')) return "checkout:start";
    if (el.closest(".cs-strip, #wc-chet, [class*=chet-pill]")) return "chet:open";
    if (!a) return null;
    var href = a.getAttribute("href") || "";
    if (/^https?:/i.test(href) && a.host !== location.host) {
      var h = a.host.replace(/^www\./, "").split(".")[0];
      return "ext:" + h.slice(0, 40);
    }
    var m;
    if ((m = href.match(/^\/products\/([a-zA-Z0-9\-_]+)/))) return "product:" + m[1].slice(0, 50);
    if ((m = href.match(/^\/collections\/([a-zA-Z0-9\-_]+)/))) return "coll:" + m[1].slice(0, 50);
    if ((m = href.match(/^\/pages\/([a-zA-Z0-9\-_]+)/))) return "page:" + m[1].slice(0, 50);
    if (href === "/" ) return "nav:home";
    if (href.indexOf("/cart") === 0) return "nav:cart";
    if (href.indexOf("/search") === 0) return "nav:search";
    return null; // untracked link classes are fine — coverage beats completeness here
  }
  addEventListener("click", function (ev) {
    try {
      var k = keyFor(ev.target);
      if (!k || !KEY_RE.test(k)) return;
      if (IMMEDIATE[k]) fire(k); else clicks[k] = (clicks[k] || 0) + 1;
    } catch (e) { /* never interfere with the click itself */ }
  }, { capture: true, passive: true });

  /* ---------------------------------------------------------------------------------------
     CART:ADD IS NOT A CLICK. It used to be one — `form[action^="/cart/add"] [type=submit],
     [name=add]` — and it recorded exactly zero adds, ever, on either skin, because nothing a
     shopper actually presses matches it. Measured on the live day-file: 24 sessions, 22 click
     keys, no `cart:add` among them. Every custom surface (wc-product's #pd-add and #pd-fbtadd,
     wc-plain-product's #pp-atc/#pp-sticky-atc, wc-aisle, wc-feed, wc-lobby, wc-hats, wc-cart's
     recommendations, the header drawer) POSTs /cart/add.js itself and never renders a
     `name="add"` button. Chasing that with a list of element ids would be a list that goes
     stale the next time a section is written.

     So the signal is taken where it is TRUE: a successful POST to /cart/add. That is one
     number that cannot drift out of date with the markup, it covers both skins and every
     surface at once, and it counts adds rather than presses — a press that 500s is not an
     add, and under a misrepresentation review the difference is the whole point.

     The patch CHAINS (calls through to whatever fetch was there), the same way
     wc-cart-feedback.liquid and wc-attribution.liquid already do; all three see every call. */
  try {
    var _fetch = window.fetch;
    if (typeof _fetch === "function") {
      window.fetch = function (input, init) {
        var res = _fetch.apply(this, arguments);   // throws exactly as the original would
        try {
          var url = (typeof input === "string" ? input : (input && input.url)) || "";
          if (/\/cart\/add(\.js)?(\?|$)/.test(url) && res && typeof res.then === "function") {
            return res.then(function (r) { if (r && r.ok) fire("cart:add"); return r; });
          }
        } catch (e) { /* measurement never breaks a request */ }
        return res;
      };
    }
  } catch (e) {}

  /* Form submits: the classic add-to-cart, and email capture on both skins.
     Passive and capture — this listener reads, it never cancels. */
  addEventListener("submit", function (ev) {
    try {
      var f = ev.target;
      if (!f || f.nodeName !== "FORM") return;
      if (/\/cart\/add/.test(f.getAttribute("action") || "")) {
        /* wc-cart-feedback.liquid converts these submits into a fetch, which the observer
           above already counts. It is not rendered by theme.plain.liquid, so on the plain
           skin a native post really does leave the page — count it here instead. Checking
           the flag at event time (not at boot) makes this exact either way. */
        if (!window.__wcCartFeedback) fire("cart:add");
        return;
        /* One gap, deliberately left: if that snippet's own fetch FAILS it sets
           data-wc-native and re-submits the form natively, and neither route counts it. The
           observer saw no ok response and this branch already declined. Undercounting an add
           that only landed on a failure retry is the conservative direction and keeps the
           rule intact — the number is adds the server accepted, from one place. */
      }
      /* EMAIL CAPTURE, measured at SUBMIT — the moment a visitor hands over an address that
         passed the field's own validation. Not at the server's answer: the Shopify newsletter
         form ({% form 'customer' %}, used by the footer, the lobby dispatch, the appeal, the
         steam notify and the plain home page) POSTs and navigates, so there is no response to
         observe from here. The two perks desks (#rf-form weird, #pr-form plain) post to
         /api/refer over fetch. One rule for both, so the number means one thing.
         A new email form joins this by carrying data-wc-track="email:signup". */
      if (
        f.getAttribute("data-wc-track") === "email:signup" ||
        f.id === "rf-form" || f.id === "pr-form" ||
        f.querySelector('input[name="form_type"][value="customer"]')
      ) fire("email:signup");
    } catch (e) { /* never interfere with the submit itself */ }
  }, { capture: true, passive: true });

  // ---- one beacon, when the page is genuinely going away ----
  function send() {
    if (sent) return;
    sent = true;
    try {
      var payload = {
        // Plain-mode views report under a /plain prefix: api/track's PATH_RE cannot carry
        // a query string, and the weird-vs-plain comparison is the entire point of the mode
        // toggle — same path shape either side, so /products/x and /plain/products/x align.
        // pathNow() is shared with the single-event channel so both land in the same column.
        p: pathNow(),
        newSession: newSession,
        dur: Math.round((Date.now() - t0) / 1000),
        scroll: maxScroll,
        clicks: clicks,
      };
      /* Set rather than always present, so an unstamped visitor sends no key at all and the
         server cannot mistake a default for an observation. */
      if (modeSrc) payload.src = modeSrc;
      navigator.sendBeacon(URL, JSON.stringify(payload));
    } catch (e) { /* losing one pageview is fine */ }
  }
  addEventListener("pagehide", send);
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") send();
  });
  }
})();
